/**
 * Logout Detector for named_fe_mobile
 * 
 * Detects when user logs out and triggers a callback
 * - Monitors session_id cookie changes/removal
 * - Listens to localStorage broadcasts from other tabs
 * - Listens to window.postMessage events (compatible with named_web)
 * 
 * Usage:
 *   import { initLogoutDetector } from './common/utils/logoutDetector';
 *   
 *   initLogoutDetector({
 *     onLogout: () => window.location.reload(),
 *     intervalMs: 15000
 *   });
 */

import Cookie from './cookie';

/**
 * Get current session ID from cookies
 * Checks both 'session_id' (named_fe_mobile) and 'PHPSESSID' (named_web)
 */
function getSessionId() {
  return Cookie.getCookie('session_id') || Cookie.getCookie('PHPSESSID') || null;
}

/**
 * Broadcast logout event to other tabs via localStorage
 */
function broadcastLogout() {
  try {
    localStorage.setItem('named_logout_event', String(Date.now()));
  } catch (e) {
    console.warn('Failed to broadcast logout:', e);
  }
}

/**
 * Initialize logout detector
 * 
 * @param {Object} options - Configuration options
 * @param {Function} options.onLogout - Callback when logout is detected (default: reload page)
 * @param {Number} options.intervalMs - Polling interval in milliseconds (default: 15000)
 * @returns {Function} Cleanup function to stop the detector
 */
export function initLogoutDetector({ onLogout, intervalMs = 15000 } = {}) {
  // Only works in browser environment
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => {};
  }

  // Default to page reload if no callback provided
  if (typeof onLogout !== 'function') {
    onLogout = () => {
      console.log('Logout detected - reloading page...');
      window.location.reload();
    };
  }

  let currentSessionId = getSessionId();
  let timerId = null;
  let hasTriggered = false; // Prevent multiple triggers
  let isStopped = false; // Flag to stop detector (e.g., when WebSocket handles logout)

  /**
   * Stop the logout detector (called by WebSocket when it handles force logout)
   */
  function stop() {
    isStopped = true;
    stopPolling();
    removeListeners();
  }

  /**
   * Trigger logout callback
   */
  function triggerLogout(reason = 'unknown') {
    if (hasTriggered) return; // Already triggered
    if (isStopped) return; // Detector was stopped (WebSocket is handling it)
    
    // Check if WebSocket is handling force logout
    if (window.forceLogoutInProgress) {
      return;
    }
    
    hasTriggered = true;

    console.log(`Logout detected: ${reason}`);
    stopPolling();
    removeListeners();
    
    // Add a small delay to give WebSocket a chance to handle force logout first
    // This is especially important when session is deleted server-side
    if (reason === 'session_removed') {
      setTimeout(() => {
        if (isStopped || window.forceLogoutInProgress) {
          return;
        }
        onLogout();
      }, 2000);
    } else {
      // Double check before triggering logout
      if (window.forceLogoutInProgress) {
        return;
      }
      onLogout();
    }
  }

  /**
   * Start polling for session changes
   * NOTE: We don't poll session cookies anymore to avoid race condition with WebSocket force logout.
   * Instead, we rely on WebSocket messages and localStorage events from other tabs.
   * This matches the behavior of named_web's logout-detector.js
   */
  function startPolling() {
    // Removed session cookie polling to avoid race condition with WebSocket
    // WebSocket will handle force logout in real-time
    // This detector now only handles:
    // 1. localStorage events from other tabs
    // 2. window.postMessage events
    console.log(`Logout detector started (listening to localStorage and postMessage events only)`);
  }

  /**
   * Stop polling
   */
  function stopPolling() {
    if (timerId) {
      clearInterval(timerId);
      timerId = null;
    }
  }

  /**
   * Handle localStorage events from other tabs
   * Detects when another tab broadcasts logout
   */
  function onStorageEvent(e) {
    if (!e || !e.key) return;
    
    // Skip if force logout is in progress (handled by WebSocket)
    if (window.forceLogoutInProgress) {
      return;
    }

    // Logout broadcast from another tab
    if (e.key === 'named_logout_event') {
      triggerLogout('broadcast_from_other_tab');
    }
    
    // Direct session_id removal (rare but possible)
    if (e.key === 'session_id' && !e.newValue) {
      triggerLogout('session_removed_via_storage');
    }
  }

  /**
   * Handle window.postMessage events
   * Compatible with named_web's postMessage API
   */
  function onMessageEvent(e) {
    if (!e || !e.data) return;
    
    // Skip if force logout is in progress (handled by WebSocket)
    if (window.forceLogoutInProgress) {
      return;
    }

    const data = e.data;

    // named_web uses 'commend' (typo for 'command')
    if (data.commend === 'update_user_session') {
      triggerLogout('postmessage_update_user_session');
    }

    // Also check for correct spelling
    if (data.command === 'logout' || data.type === 'LOGOUT') {
      triggerLogout('postmessage_logout');
    }
  }

  /**
   * Remove all event listeners
   */
  function removeListeners() {
    window.removeEventListener('storage', onStorageEvent);
    window.removeEventListener('message', onMessageEvent);
  }

  /**
   * Cleanup function
   */
  function cleanup() {
    stopPolling();
    removeListeners();
    hasTriggered = false;
    isStopped = false;
  }

  // Initialize
  window.addEventListener('storage', onStorageEvent);
  window.addEventListener('message', onMessageEvent);
  startPolling();

  // Return cleanup function with stop method
  const cleanupFn = cleanup;
  cleanupFn.stop = stop;
  return cleanupFn;
}

/**
 * Notify other tabs that user has logged out
 * Call this when user explicitly logs out in this tab
 */
export function notifyLoggedOut() {
  broadcastLogout();
}

/**
 * Check if user is currently logged in
 * @returns {Boolean}
 */
export function isLoggedIn() {
  return !!getSessionId();
}

export default {
  initLogoutDetector,
  notifyLoggedOut,
  isLoggedIn
};

